import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {RepositoryLabelsInternal} from '@github-ui/repository-label/RepositoryLabels'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {useQueryContext} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import type {RepositoryLabelIndexPageContentInternal$key} from './__generated__/RepositoryLabelIndexPageContentInternal.graphql'
import type {RepositoryLabelIndexPageQuery} from './__generated__/RepositoryLabelIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryLabelIndexPageQuery(
    $name: String!
    $owner: String!
    $first: Int!
    $orderField: LabelOrderField = NAME
    $orderDirection: OrderDirection = ASC
    $skip: Int!
    $query: String
  ) {
    repository(owner: $owner, name: $name) {
      ...RepositoryLabelIndexPageContentInternal
        @arguments(first: $first, orderField: $orderField, orderDirection: $orderDirection, skip: $skip, query: $query)
    }
  }
`

export const RepositoryLabelIndexPage: EntryPointComponent<
  {pageQuery: RepositoryLabelIndexPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null
  return (
    <AnalyticsWrapper category="Repository Label Index">
      <RepositoryLabelIndexContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryLabelIndexContent({pageQueryRef}: {pageQueryRef: PreloadedQuery<RepositoryLabelIndexPageQuery>}) {
  const pageData = usePreloadedQuery<RepositoryLabelIndexPageQuery>(PageQuery, pageQueryRef)

  const {setCurrentViewId} = useQueryContext()

  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  if (!pageData.repository) {
    reportError(
      new Error(`Could not find repository when loading labels index for ${ssrSafeLocation?.href.toString()}`),
    )
    return <div>Repository not found</div>
  }
  return <RepositoryLabelIndexPageContentInternal repository={pageData.repository} />
}

function RepositoryLabelIndexPageContentInternal({
  repository,
}: {
  repository: RepositoryLabelIndexPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryLabelIndexPageContentInternal on Repository
      @argumentDefinitions(
        first: {type: "Int!"}
        orderField: {type: "LabelOrderField", defaultValue: NAME}
        orderDirection: {type: "OrderDirection", defaultValue: ASC}
        skip: {type: "Int!"}
        query: {type: "String"}
      ) {
        ...RepositoryLabelsInternal
          @arguments(
            first: $first
            orderField: $orderField
            orderDirection: $orderDirection
            skip: $skip
            query: $query
          )
      }
    `,
    repository,
  )
  return <RepositoryLabelsInternal repository={data} />
}

try{ RepositoryLabelIndexContent.displayName ||= 'RepositoryLabelIndexContent' } catch {}
try{ RepositoryLabelIndexPageContentInternal.displayName ||= 'RepositoryLabelIndexPageContentInternal' } catch {}